/**
 * Recharge & Wallets Service Module
 *
 * Manages recharge operations and wallet management.
 */

import { api } from './client';
import type {
  RechargeRequest,
  RechargeResponse,
  RechargeProofRequest,
  RechargeWallet,
  ListResponse,
  PaginationParams,
} from './types';

/**
 * Recharge API
 *
 * Endpoints for recharge and wallet operations.
 */
export const rechargeAPI = {
  /**
   * Get recharge requests
   *
   * Retrieves user's recharge requests.
   *
   * **Endpoint:** GET /api/recharge/
   * **Protected:** Yes
   */
  getRequests: async (params?: PaginationParams): Promise<ListResponse<RechargeResponse>> =>
    api.get<ListResponse<RechargeResponse>>('/api/recharge', params),

  /**
   * Create recharge request
   *
   * Creates a new recharge request.
   *
   * **Endpoint:** POST /api/recharge/
   * **Protected:** Yes
   */
  create: async (request: RechargeRequest): Promise<RechargeResponse> =>
    api.post<RechargeResponse>('/api/recharge', request),

  createOrder: async (body: {
    amount: string;
    method: string;
    asset: string;
  }) => api.post<{ id: number }>('/api/recharge/orders', body),

  submitOrderProof: async (orderId: number, body: { proof_url: string }) =>
    api.post<Record<string, unknown>>(`/api/recharge/orders/${orderId}/proof`, body),

  /**
   * Submit recharge proof
   *
   * Submits proof of payment for a recharge request.
   *
   * **Endpoint:** POST /api/recharge/proof
   * **Protected:** Yes
   */
  submitProof: async (request: RechargeProofRequest): Promise<{ success: boolean }> =>
    api.post<{ success: boolean }>('/api/recharge/proof', request),

  /**
   * Get available wallets
   *
   * Retrieves list of available recharge wallets.
   *
   * **Endpoint:** GET /api/recharge-wallets/
   * **Protected:** No
   */
  getWallets: async (): Promise<RechargeWallet[]> =>
    api.get<RechargeWallet[]>('/api/recharge-wallets'),

  getTpOnchainConfig: (params: { chain_id: number }) =>
    api.get<{
      enabled: boolean;
      min_tp: string;
      tp_step: number;
      exchange_rate: string;
      rate: string;
      chain_id: number;
      usdt_contract: string | null;
      usdt_decimals: number | null;
      mock_pay_allowed?: boolean;
      bound_wallet: string | null;
      user_id: number;
      username: string;
      wallet: {
        id: number;
        name: string;
        address: string;
        chain_id: string;
        currency: string;
      } | null;
    }>('/api/recharge/tp/config', params),

  createTpPending: (body: { amount_tp: string; chain_id: number; from_address?: string }) =>
    api.post<{
      id: number;
      amount_tp: string;
      payment_usdt: string;
      exchange_rate: string;
      status: string;
    }>('/api/recharge/tp/pending', body),

  getTpRecords: (params?: PaginationParams) =>
    api.get<{
      total: number;
      rows: Array<{
        id: number;
        create_time: string;
        payment_usdt: string;
        amount_tp: string;
        status: string;
        legacy_status: string;
        tx_hash: string | null;
      }>;
    }>('/api/recharge/tp/records', params),

  submitTpOnchain: (body: {
    amount_tp?: string;
    amount_usdt?: string;
    tx_hash: string;
    chain_id: number;
    from_address: string;
    wallet_config_id?: number;
    order_id?: number;
  }) =>
    api.post<{
      id: number;
      status: string;
      amount_tp: string;
      payment_usdt?: string;
      tx_hash?: string;
    }>('/api/recharge/tp/onchain', body),

  getRpOnchainConfig: (params: { chain_id: number }) =>
    api.get<{
      enabled: boolean;
      min_rp: string;
      rp_step: number;
      exchange_rate: string;
      rate: string;
      chain_id: number;
      usdt_contract: string | null;
      usdt_decimals: number | null;
      bound_wallet: string | null;
      user_id: number;
      username: string;
      wallet: {
        id: number;
        name: string;
        address: string;
        chain_id: string;
        currency: string;
      } | null;
    }>('/api/recharge/rp/config', params),

  createRpPending: (body: { amount_rp: string; chain_id: number; from_address?: string }) =>
    api.post<{
      id: number;
      amount_rp: string;
      payment_usdt: string;
      exchange_rate: string;
      status: string;
    }>('/api/recharge/rp/pending', body),

  getRpRecords: (params?: PaginationParams) =>
    api.get<{
      total: number;
      rows: Array<{
        id: number;
        create_time: string;
        payment_usdt: string;
        amount_rp: string;
        status: string;
        legacy_status: string;
        tx_hash: string | null;
      }>;
    }>('/api/recharge/rp/records', params),

  submitRpOnchain: (body: {
    amount_rp?: string;
    amount_usdt?: string;
    tx_hash: string;
    chain_id: number;
    from_address: string;
    wallet_config_id?: number;
    order_id?: number;
  }) =>
    api.post<{
      id: number;
      status: string;
      amount_rp: string;
      payment_usdt?: string;
      tx_hash?: string;
    }>('/api/recharge/rp/onchain', body),

  getFcOnchainConfig: (params: { chain_id: number }) =>
    api.get<{
      enabled: boolean;
      min_fc: string;
      fc_step: number;
      exchange_rate: string;
      rate: string;
      chain_id: number;
      usdt_contract: string | null;
      usdt_decimals: number | null;
      bound_wallet: string | null;
      user_id: number;
      username: string;
      wallet: {
        id: number;
        name: string;
        address: string;
        chain_id: string;
        currency: string;
      } | null;
    }>('/api/recharge/fc/config', params),

  createFcPending: (body: { amount_fc: string; chain_id: number; from_address?: string }) =>
    api.post<{
      id: number;
      amount_fc: string;
      payment_usdt: string;
      exchange_rate: string;
      status: string;
      wallet?: { id: number; address: string; name: string };
    }>('/api/recharge/fc/pending', body),

  getFcRecords: (params?: PaginationParams) =>
    api.get<{
      total: number;
      rows: Array<{
        id: number;
        create_time: string;
        payment_usdt: string;
        amount_fc: string;
        status: string;
        legacy_status: string;
        tx_hash: string | null;
      }>;
    }>('/api/recharge/fc/records', params),

  submitFcOnchain: (body: {
    amount_fc?: string;
    amount_usdt?: string;
    tx_hash: string;
    chain_id: number;
    from_address: string;
    wallet_config_id?: number;
    order_id?: number;
  }) =>
    api.post<{
      id: number;
      status: string;
      amount_fc: string;
      payment_usdt?: string;
      tx_hash?: string;
    }>('/api/recharge/fc/onchain', body),

  mockTpPay: (body: { amount_tp: string; chain_id: number; order_id?: number }) =>
    api.post<{
      id: number;
      status: string;
      amount_tp: string;
      payment_usdt?: string;
      tx_hash?: string;
      mock?: boolean;
    }>('/api/recharge/tp/mock-pay', body),
};
